import type { ShortlistGroup } from '@elara/client-core/models/ShortlistGroupModel';
import type { ShortlistItem } from '@elara/client-core/models/ShortlistItemModel';

import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useState } from 'react';

import { calculateTotalsByCurrency, cn } from '@elara/client-core/lib/utils';

import { useShortlistState, useShortlistUIState } from '#states/hooks.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '#ui';
import { Button } from '#ui/button.js';
import { CostTotals } from '#ui/CostTotals.js';
import { Input } from '#ui/input.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { ShortlistItemCard, ShortlistItemCardPlaceholder } from '#ui/shortlist/ShortlistItemCard.js';

interface ShortlistItemsGroupProps {
  group: ShortlistGroup;
  draggedItemId?: string | null;
  draggedItemGroupId?: string | null;
  dropPreviewIndex?: number;
  draggedItem?: ShortlistItem | null;
}

export function ShortlistItemsGroup({
  group,
  draggedItemId,
  draggedItemGroupId,
  dropPreviewIndex = -1,
  draggedItem,
}: ShortlistItemsGroupProps): React.ReactNode {
  const uiState = useShortlistUIState();
  const shortlistState = useShortlistState();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);

  const groups = shortlistState.groups;
  const groupItems = shortlistState.itemsByGroup[group.id] || [];

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: group.id,
    data: { type: 'container', children: groupItems.map((i) => i.id) },
  });

  const { setNodeRef: setDroppableRef } = useDroppable({ id: group.id });

  const handleRenameGroup = async (groupId: string) => {
    if (!uiState.editGroupName.trim()) return;
    const newName = uiState.editGroupName.trim();

    // Exit edit mode immediately for optimistic update
    uiState.stopEditingGroup();
    setIsRenaming(true);

    try {
      await shortlistState.renameGroup(groupId, newName);
    } catch (error) {
      // On error, switch back to edit mode with the new name
      uiState.startEditingGroup(groupId, newName);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    // Don't allow deleting the last group
    if (groups.length <= 1) return;
    setIsDeleting(true);
    try {
      await shortlistState.deleteGroup(groupId);
    } finally {
      setIsDeleting(false);
    }
  };

  // Combine refs for both sortable and droppable
  const combinedRef = (node: HTMLElement | null) => {
    setNodeRef(node);
    setDroppableRef(node);
  };

  // Use uiState to determine highlight state - this works for both direct group hover and item hover
  const isDragOver = uiState.dragOverGroupId === group.id;
  const isInvalidDrop = uiState.invalidDropGroupId === group.id;

  // Check if we should show drop preview (dragging item from different group)
  // Only show preview in the target group when dragging over it, not in the original group
  const showDropPreview =
    draggedItemId && draggedItemGroupId && draggedItemGroupId !== group.id && isDragOver && dropPreviewIndex >= 0;

  // Hide the dragged item in its original group when dragging over a different group
  // This prevents SortableContext from showing placeholders in the original group
  const isOriginalGroup = Boolean(draggedItemGroupId && draggedItemGroupId === group.id);
  // Check if we're dragging over ANY different group (not just this one)
  const isDraggingOverAnyDifferentGroup = Boolean(
    draggedItemId && draggedItemGroupId && uiState.dragOverGroupId && uiState.dragOverGroupId !== draggedItemGroupId,
  );
  const shouldHideOriginalItem = isOriginalGroup && isDraggingOverAnyDifferentGroup;

  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 };

  const totalsByCurrency = calculateTotalsByCurrency(groupItems);

  return (
    <div
      ref={combinedRef}
      style={style}
      className={cn(
        'relative flex flex-col gap-2 rounded-md w-full p-2 pt-1 md:p-3 md:pt-2 bg-background-secondary @lg:max-w-sm flex-shrink-0 @lg:overflow-y-auto transition-colors',
        isDragOver && 'border-primary bg-green-500/5',
        isInvalidDrop && 'border-destructive/50 bg-destructive/5 cursor-not-allowed',
      )}
    >
      <div className={cn('flex items-center justify-between')}>
        {uiState.editingGroupId === group.id ? (
          <div className="flex items-center gap-2 flex-1">
            <Input
              value={uiState.editGroupName}
              onChange={(e) => uiState.setEditGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleRenameGroup(group.id);
                if (e.key === 'Escape') {
                  uiState.stopEditingGroup();
                }
              }}
              className="h-7 text-sm"
              autoFocus
            />
            <Button
              size="icon"
              variant="icon"
              onClick={async () => handleRenameGroup(group.id)}
              tooltip="Save"
              disabled={isRenaming}
            >
              <MaterialIcon icon="check" className="text-sm" />
            </Button>
            <Button
              size="icon"
              variant="icon"
              onClick={() => {
                uiState.stopEditingGroup();
              }}
              tooltip="Cancel"
            >
              <MaterialIcon icon="close" className="text-sm" />
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <div
                ref={setActivatorNodeRef}
                {...attributes}
                {...listeners}
                className="cursor-grab touch-manipulation active:cursor-grabbing text-muted-foreground hover:text-foreground flex items-center justify-center"
              >
                <MaterialIcon icon="drag_indicator" className="text-sm" />
              </div>
              <h3
                className={cn('font-medium', shortlistState.renamingGroupIds[group.id] && 'animate-pulse')}
                onClick={() => {
                  uiState.startEditingGroup(group.id, group.name);
                }}
              >
                {group.displayName}
              </h3>
            </div>
            <div className="flex items-center gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="icon" size="icon">
                    <MaterialIcon icon="more_horiz" className="text-sm" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => uiState.startEditingGroup(group.id, group.name)}>
                    <MaterialIcon icon="edit" className="mr-2" size={IconSizes.sm} />
                    Rename
                  </DropdownMenuItem>
                  {groups.length > 1 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={async () => handleDeleteGroup(group.id)}
                        className="text-destructive"
                        disabled={isDeleting}
                      >
                        <MaterialIcon icon="delete" className="mr-2" size={IconSizes.sm} />
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        )}
      </div>

      <SortableContext
        items={
          shouldHideOriginalItem && draggedItemId
            ? groupItems.filter((i) => i.id !== draggedItemId).map((i) => i.id)
            : groupItems.map((i) => i.id)
        }
        strategy={verticalListSortingStrategy}
        disabled={shouldHideOriginalItem}
      >
        <div className="flex flex-col gap-3 flex-1">
          {groupItems
            .filter((item) => !(shouldHideOriginalItem && draggedItemId === item.id))
            .map((item, index) => {
              // Adjust index if we filtered out the dragged item
              const adjustedIndex =
                shouldHideOriginalItem && draggedItemId ? groupItems.findIndex((i) => i.id === item.id) : index;

              return (
                <React.Fragment key={item.id}>
                  {showDropPreview && dropPreviewIndex === adjustedIndex && draggedItem && (
                    <ShortlistItemCardPlaceholder item={draggedItem} groups={groups} />
                  )}
                  <ShortlistItemCard
                    item={item}
                    groups={groups}
                    isDragging={uiState.draggedItemId === item.id}
                    disableSortable={shouldHideOriginalItem && draggedItemId === item.id}
                  />
                </React.Fragment>
              );
            })}
          {showDropPreview && dropPreviewIndex === groupItems.length && draggedItem && (
            <ShortlistItemCardPlaceholder item={draggedItem} groups={groups} />
          )}
        </div>
      </SortableContext>

      {shortlistState.showTotals && (
        <div className="flex items-center justify-between mt-2">
          <div className="font-medium">Total</div>
          <CostTotals totalsByCurrency={totalsByCurrency} emptyLabel="–" className="flex items-center gap-1" />
        </div>
      )}
    </div>
  );
}
